import { unpackMint } from '@solana/spl-token';
import { type Connection, PublicKey, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import type BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  uniqueSignerKeys,
} from './common.js';

const VAULT_SEED: Buffer = Buffer.from('vault');
const PRIZE_VAULT_SEED: Buffer = Buffer.from('prize');
const PLATFORM_FEE_VAULT_SEED: Buffer = Buffer.from('platform_fee_vault');
const BACKER_SHARE_MINT_SEED: Buffer = Buffer.from('backer_share_mint');
const METADATA_SEED: Buffer = Buffer.from('metadata');
const EVENT_AUTHORITY_SEED: Buffer = Buffer.from('__event_authority');

/** mpl-token-metadata program id (used to derive metadata PDA for the backer share mint). */
const MPL_TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

interface MarketAccountV2 {
  config: PublicKey;
}

interface MarketConfigAccountV2 {
  quoteMint: PublicKey;
}

interface AnchorProgramAccounts {
  account: {
    market: { fetch(address: PublicKey): Promise<MarketAccountV2> };
    marketConfig: { fetch(address: PublicKey): Promise<MarketConfigAccountV2> };
  };
}

function derivePda(seeds: Buffer[], programId: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(seeds, programId)[0];
}

function derivePrizePoolVaultPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([VAULT_SEED, PRIZE_VAULT_SEED, market.toBuffer()], programId);
}

function derivePlatformFeeVaultPda(marketConfig: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([PLATFORM_FEE_VAULT_SEED, marketConfig.toBuffer()], programId);
}

function deriveBackerShareMintPda(market: PublicKey, programId: PublicKey): PublicKey {
  return derivePda([BACKER_SHARE_MINT_SEED, market.toBuffer()], programId);
}

/**
 * Derive the backer's classic-SPL associated-token-account for the
 * market's `backer_share_mint`. This is the account the `back` handler
 * mints fee-share tokens into and the `unback` handler burns from.
 *
 * Read-only counterpart to `buildBack` / `buildUnback` — useful when the
 * caller needs to look up the on-chain share balance (e.g. to compute
 * the exact `burnAmount` for a full unback). Pure PDA derivation; no
 * RPC call.
 */
export function deriveBackerShareAta(
  backer: PublicKey,
  market: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): PublicKey {
  const mint = deriveBackerShareMintPda(market, programId);
  return deriveAta(backer, mint);
}

function deriveBackerShareMetadataPda(backerShareMint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [METADATA_SEED, MPL_TOKEN_METADATA_PROGRAM_ID.toBuffer(), backerShareMint.toBuffer()],
    MPL_TOKEN_METADATA_PROGRAM_ID,
  )[0];
}

function deriveEventAuthorityPda(programId: PublicKey): PublicKey {
  return derivePda([EVENT_AUTHORITY_SEED], programId);
}

function deriveAta(owner: PublicKey, mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )[0];
}

async function isInitializedClassicSplMint(
  connection: Connection,
  mint: PublicKey,
): Promise<boolean> {
  const account = await connection.getAccountInfo(mint);
  if (!account) return false;
  try {
    return unpackMint(mint, account, TOKEN_PROGRAM_ID).isInitialized;
  } catch {
    return false;
  }
}

export interface BackParams extends BuildParams {
  backer: PublicKey;
  market: PublicKey;
  /** Gross quote amount to contribute (D6). A fee_bps_backing slice routes to platform fees. */
  amount: BN;
  /** Rent payer for idempotent ATA creations; defaults to backer. */
  payer?: PublicKey;
}

export interface UnbackParams extends BuildParams {
  backer: PublicKey;
  market: PublicKey;
  /** Amount of backer-fee-share tokens to burn (D6). */
  burnAmount: BN;
}

/**
 * Build a `back` transaction. Backs quote into a market's prize pool in exchange
 * for newly-minted backer-fee-share tokens. The market must be in Backing state.
 *
 * Wraps IDL instruction `back` — see contracts/markets/idl/markets.json.
 *
 * @param connection - Solana RPC connection.
 * @param params - Back parameters. See {@link BackParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildBack(connection: Connection, params: BackParams): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  // ALLOW-UNKNOWN: Anchor `Program<Idl>` and our typed `AnchorProgramAccounts` view of the same handle have structurally incompatible function signatures that TS cannot reconcile without `unknown`.
  const accounts = program as unknown as AnchorProgramAccounts;
  const backerShareMint = deriveBackerShareMintPda(params.market, programId);
  const [marketAccount, isShareMintInitialized] = await Promise.all([
    accounts.account.market.fetch(params.market),
    isInitializedClassicSplMint(connection, backerShareMint),
  ]);
  const marketConfig = await accounts.account.marketConfig.fetch(marketAccount.config);

  const quoteMint = marketConfig.quoteMint;
  const backerQuoteAta = deriveAta(params.backer, quoteMint);
  const backerShareAta = deriveAta(params.backer, backerShareMint);

  const ataPayer = params.payer ?? params.backer;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.backer, quoteMint, TOKEN_PROGRAM_ID),
  ];
  if (isShareMintInitialized) {
    preInstructions.push(
      createIdempotentAtaIx(ataPayer, params.backer, backerShareMint, TOKEN_PROGRAM_ID),
    );
  }

  const instruction = await invokeMethod(program, 'back', params.amount)
    .accounts({
      backer: params.backer,
      market: params.market,
      marketConfig: marketAccount.config,
      quoteMint,
      backerShareMint,
      backerFeeShareMetadata: deriveBackerShareMetadataPda(backerShareMint),
      prizePoolVault: derivePrizePoolVaultPda(params.market, programId),
      platformFeeVault: derivePlatformFeeVaultPda(marketAccount.config, programId),
      backerQuoteAta,
      backerShareAta,
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      tokenMetadataProgram: MPL_TOKEN_METADATA_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('back');
  return createBuildResult(
    [...preInstructions, instruction],
    uniqueSignerKeys(params.backer, ataPayer),
    computeUnits,
  );
}

/**
 * Build an `unback` transaction. Burns backer-fee-share tokens, reversing a
 * prior `back` contribution.
 *
 * Wraps IDL instruction `unback` — see contracts/markets/idl/markets.json.
 *
 * @param connection - Solana RPC connection.
 * @param params - Unback parameters. See {@link UnbackParams}.
 * @returns BuildResult with unsigned transaction ready for signing.
 */
export async function buildUnback(
  connection: Connection,
  params: UnbackParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  // ALLOW-UNKNOWN: Anchor `Program<Idl>` and our typed `AnchorProgramAccounts` view of the same handle have structurally incompatible function signatures that TS cannot reconcile without `unknown`.
  const accounts = program as unknown as AnchorProgramAccounts;
  const marketAccount = await accounts.account.market.fetch(params.market);
  const marketConfig = await accounts.account.marketConfig.fetch(marketAccount.config);

  const quoteMint = marketConfig.quoteMint;
  const backerShareMint = deriveBackerShareMintPda(params.market, programId);
  const backerQuoteAta = deriveAta(params.backer, quoteMint);
  const backerShareAta = deriveAta(params.backer, backerShareMint);

  const instruction = await invokeMethod(program, 'unback', params.burnAmount)
    .accounts({
      backer: params.backer,
      market: params.market,
      marketConfig: marketAccount.config,
      backerShareMint,
      prizePoolVault: derivePrizePoolVaultPda(params.market, programId),
      platformFeeVault: derivePlatformFeeVaultPda(marketAccount.config, programId),
      backerQuoteAta,
      backerShareAta,
      tokenProgram: TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId),
      program: programId,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('unback');
  return createBuildResult([instruction], [params.backer], computeUnits);
}
